/**
 * dev122 — Estado de "leído/no leído" para notificaciones, persistido en
 * localStorage por usuario. Sin DB.
 *
 * Estrategia:
 *  - Key por user-id: `byteisp:notifs:read:{userId}` o `:anon` si no hay user.
 *  - Set de strings con los IDs marcados como leídos.
 *  - Cleanup automático: cuando recibimos el feed actual, descartamos IDs
 *    leídos que YA NO están en el feed (porque la ventana de tiempo del
 *    backend ya los expiró). Eso evita que el localStorage crezca infinito.
 *  - Cap de seguridad: máximo 500 IDs. Si excede, droppear los primeros
 *    insertados (FIFO).
 *
 * Decisión de diseño: NO usar DB porque (1) el feed mismo es no-persistente,
 * (2) un admin típicamente trabaja desde 1-2 máquinas, (3) si se cambia de
 * browser pierde el estado pero solo significa que vuelve a ver lo de hoy
 * — no es catastrófico. Si se vuelve un problema real, en v1.1 se promueve
 * a tabla per-user.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthStore } from "@/lib/auth";

const MAX_IDS = 500;

function storageKey(userId: string | null): string {
  return `byteisp:notifs:read:${userId ?? "anon"}`;
}

function load(userId: string | null): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((x): x is string => typeof x === "string"));
  } catch {
    return new Set();
  }
}

function save(userId: string | null, ids: Set<string>): void {
  try {
    let arr = Array.from(ids);
    // Cap FIFO: si excede, descartar los más viejos del array (que es el orden de inserción)
    if (arr.length > MAX_IDS) {
      arr = arr.slice(arr.length - MAX_IDS);
    }
    localStorage.setItem(storageKey(userId), JSON.stringify(arr));
  } catch {
    // localStorage podría estar lleno o deshabilitado — fallar silencioso
  }
}

export function useReadNotifications(currentIds: string[]) {
  const user = useAuthStore((s) => s.user);
  const userId = user?.id ?? null;

  const [readIds, setReadIds] = useState<Set<string>>(() => load(userId));

  // Cleanup: cuando cambian los IDs del feed actual, descartar leídos
  // que ya no están en el feed (expiraron del backend).
  useEffect(() => {
    if (currentIds.length === 0) return;
    const liveSet = new Set(currentIds);
    setReadIds((prev) => {
      const filtered = new Set([...prev].filter((id) => liveSet.has(id)));
      // Solo persistir si cambió
      if (filtered.size !== prev.size) {
        save(userId, filtered);
        return filtered;
      }
      return prev;
    });
  }, [currentIds, userId]);

  // Re-load si el user cambia (login/logout dentro del mismo tab)
  useEffect(() => {
    setReadIds(load(userId));
  }, [userId]);

  const markRead = useCallback((id: string) => {
    setReadIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      save(userId, next);
      return next;
    });
  }, [userId]);

  const markAllRead = useCallback((ids: string[]) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      save(userId, next);
      return next;
    });
  }, [userId]);

  const isRead = useCallback((id: string) => readIds.has(id), [readIds]);

  const unreadCount = useMemo(
    () => currentIds.filter((id) => !readIds.has(id)).length,
    [currentIds, readIds],
  );

  return { isRead, markRead, markAllRead, unreadCount };
}
